import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import { toast } from 'react-toastify';
import { 
  FaPlus, 
  FaSearch, 
  FaEdit, 
  FaTrash, 
  FaTimes, 
  FaCheckCircle, 
  FaExclamationTriangle, 
  FaShieldAlt,
  FaIndustry
} from 'react-icons/fa';

import { 
  getProjectWarranties, 
  createWarranty, 
  updateWarranty, 
  deleteWarranty,
  WarrantyInterface,
  WarrantyCreateData,
  WarrantyUpdateData
} from '../api/warranties';
import { getProjectMaterials, MaterialTrackerItem } from '../api/materialTracker';
import { getManufacturers, ManufacturerInterface } from '../api/manufacturers';
import { ManufacturerManager } from '../components/Project/ManufacturerManager';
import { getTableHeaderStyle, DesignSystem } from '../styles/designSystem';
import { LoadingSpinner } from '../components/LoadingSpinner';

export const Warranty = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { keycloak } = useKeycloak();
  const token = keycloak?.token;

  const [warranties, setWarranties] = useState<WarrantyInterface[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [manufacturerFilter, setManufacturerFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingWarranty, setEditingWarranty] = useState<WarrantyInterface | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<string | null>(null);
  const [showManufacturerManager, setShowManufacturerManager] = useState(false);

  // Data for dropdowns
  const [projectMaterials, setProjectMaterials] = useState<MaterialTrackerItem[]>([]);
  const [availableManufacturers, setAvailableManufacturers] = useState<ManufacturerInterface[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(false);

  // Form state interface for UI (unit_id is string for input handling)
  interface WarrantyFormData {
    project_material_id: string;
    unit_id: string;
    manufacturer_id: string;
    warranty_start_date: string;
    warranty_end_date: string;
    warranty_provider: string;
    warranty_terms: string;
    coverage_details: string;
    serial_number: string;
    model: string;
  }

  // Form state for create/edit modal
  const [formData, setFormData] = useState<WarrantyFormData>({
    project_material_id: '',
    unit_id: '',
    manufacturer_id: '',
    warranty_start_date: '',
    warranty_end_date: '',
    warranty_provider: '',
    warranty_terms: '',
    coverage_details: '',
    serial_number: '',
    model: ''
  });

  // Scroll detection for sticky header
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Load warranties and dropdown data on mount. Guard against React
  // StrictMode's dev-only double-invoke firing this load twice, while still
  // reloading on a genuine projectId/token change (this component does not
  // remount when switching projects while already on the Warranties tab).
  // getProjectWarranties genuinely uses `token` (it attaches it manually,
  // bypassing authenticatedFetch), so it must stay in the guard key.
  const lastLoadedKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!projectId || !token) return;
    const key = `${projectId}|${token}`;
    if (lastLoadedKeyRef.current === key) return;
    lastLoadedKeyRef.current = key;
    loadWarranties();
    loadDropdownData();
  }, [projectId, token]);

  // Load warranties from API
  const loadWarranties = async () => {
    if (!token || !projectId) return;

    try {
      setIsLoading(true);
      console.log('Loading warranties for project:', projectId);
      
      const response = await getProjectWarranties(
        token, 
        projectId, 
        1, 
        100, 
        manufacturerFilter !== 'all' ? manufacturerFilter : undefined
      );
      
      console.log('Warranties API response:', response);
      setWarranties(response.results || []);
    } catch (error) {
      console.error('Failed to load warranties:', error);
      toast.error('Failed to load warranties');
    } finally {
      setIsLoading(false);
    }
  };

  // Load dropdown data for form
  const loadDropdownData = async () => {
    if (!token || !projectId) return;

    try {
      setIsLoadingData(true);
      
      // Load project materials
      const materialsResponse = await getProjectMaterials(token, projectId);
      setProjectMaterials(materialsResponse.results || []);
      
      // Load manufacturers
      const manufacturersResponse = await getManufacturers(token, 1, 100);
      setAvailableManufacturers(manufacturersResponse.results || []);
      
    } catch (error) {
      console.error('Failed to load dropdown data:', error);
      // Don't show error toast as this is secondary data
    } finally {
      setIsLoadingData(false);
    }
  };

  // Manufacturers are now loaded from API in loadDropdownData

  // Filter warranties based on search and filters
  const filteredWarranties = useMemo(() => {
    return warranties.filter(warranty => {
      // Search filter
      const matchesSearch = !searchQuery || 
        warranty.serial_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
        warranty.model.toLowerCase().includes(searchQuery.toLowerCase()) ||
        warranty.warranty_provider.toLowerCase().includes(searchQuery.toLowerCase()) ||
        warranty.manufacturer.name.toLowerCase().includes(searchQuery.toLowerCase());

      // Manufacturer filter
      const matchesManufacturer = manufacturerFilter === 'all' || warranty.manufacturer_id === manufacturerFilter;

      // Status filter
      const matchesStatus = statusFilter === 'all' || 
        (statusFilter === 'active' && warranty.is_active_warranty) ||
        (statusFilter === 'expired' && !warranty.is_active_warranty) ||
        (statusFilter === 'expiring' && warranty.is_active_warranty && warranty.days_remaining <= 30);

      return matchesSearch && matchesManufacturer && matchesStatus;
    });
  }, [warranties, searchQuery, manufacturerFilter, statusFilter]);

  // Get unique manufacturers for filter
  const uniqueManufacturers = useMemo(() => {
    const manufacturers = warranties.map(w => ({ 
      id: w.manufacturer_id, 
      name: w.manufacturer.name 
    }));
    const unique = manufacturers.filter((m, index, self) => 
      index === self.findIndex(t => t.id === m.id)
    );
    return unique;
  }, [warranties]);

  // Get warranty status info
  const getWarrantyStatus = (warranty: WarrantyInterface) => {
    if (!warranty.is_active_warranty) {
      return { label: 'Expired', color: 'text-red-600 bg-red-50', icon: FaExclamationTriangle };
    } else if (warranty.days_remaining <= 30) {
      return { label: `${warranty.days_remaining} days left`, color: 'text-yellow-600 bg-yellow-50', icon: FaExclamationTriangle };
    } else {
      return { label: `${warranty.days_remaining} days left`, color: 'text-green-600 bg-green-50', icon: FaCheckCircle };
    }
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Validate UUID format
  const isValidUUID = (uuid: string) => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(uuid);
  };

  // Validate form before submission
  const validateForm = () => {
    if (!formData.serial_number) {
      toast.error('Serial number is required');
      return false;
    }
    if (!formData.model) {
      toast.error('Model is required');
      return false;
    }
    if (!formData.manufacturer_id) {
      toast.error('Manufacturer is required');
      return false;
    }
    if (!formData.warranty_start_date) {
      toast.error('Warranty start date is required');
      return false;
    }
    if (!formData.warranty_end_date) {
      toast.error('Warranty end date is required');
      return false;
    }
    if (!formData.warranty_provider) {
      toast.error('Warranty provider is required');
      return false;
    }
    if (formData.unit_id && !isValidUUID(formData.unit_id)) {
      toast.error('Unit ID must be a valid UUID format');
      return false;
    }
    return true;
  };

  // Handle create warranty
  const handleCreate = async () => {
    if (!validateForm() || !token || !projectId) return;

    try {
      console.log('🔧 Creating warranty with data:', formData);
      
      const warrantyData: WarrantyCreateData = {
        project_material_id: formData.project_material_id || null,
        unit_id: formData.unit_id || null,
        manufacturer_id: formData.manufacturer_id,
        warranty_start_date: formData.warranty_start_date,
        warranty_end_date: formData.warranty_end_date,
        warranty_provider: formData.warranty_provider,
        warranty_terms: formData.warranty_terms || '',
        coverage_details: formData.coverage_details || '',
        serial_number: formData.serial_number,
        model: formData.model
      };

      console.log('📤 Sending warranty data to API:', warrantyData);
      
      const warrantyId = await createWarranty(token, projectId, warrantyData);
      console.log('✅ Warranty created successfully with ID:', warrantyId);
      
      toast.success('Warranty created successfully');
      setShowCreateModal(false);
      resetForm();
      await loadWarranties();
    } catch (error: any) {
      console.error('❌ Failed to create warranty:', error);
      console.error('Error details:', error.message);
      toast.error(`Failed to create warranty: ${error.message || 'Unknown error'}`);
    }
  };

  // Handle update warranty
  const handleUpdate = async () => {
    if (!validateForm() || !token || !projectId || !editingWarranty) return;

    try {
      console.log('🔧 Updating warranty with data:', formData);
      console.log('📋 Original warranty:', editingWarranty);
      
      const warrantyData: WarrantyUpdateData = {
        id: editingWarranty.id,
        project_material_id: formData.project_material_id || editingWarranty.project_material_id || null,
        unit_id: formData.unit_id || editingWarranty.unit_id,
        manufacturer_id: formData.manufacturer_id,
        warranty_start_date: formData.warranty_start_date,
        warranty_end_date: formData.warranty_end_date,
        warranty_provider: formData.warranty_provider,
        warranty_terms: formData.warranty_terms || '',
        coverage_details: formData.coverage_details || '',
        serial_number: formData.serial_number,
        model: formData.model
      };

      console.log('📤 Sending updated warranty data to API:', warrantyData);

      await updateWarranty(token, projectId, editingWarranty.id, warrantyData);
      console.log('✅ Warranty updated successfully');
      
      toast.success('Warranty updated successfully');
      setEditingWarranty(null);
      resetForm();
      await loadWarranties();
    } catch (error: any) {
      console.error('❌ Failed to update warranty:', error);
      console.error('Error details:', error.message);
      toast.error(`Failed to update warranty: ${error.message || 'Unknown error'}`);
    }
  };

  // Handle delete warranty
  const handleDelete = async (warrantyId: string) => {
    if (!token || !projectId) return;

    try {
      await deleteWarranty(token, projectId, warrantyId);
      toast.success('Warranty deleted successfully');
      setShowDeleteModal(null);
      await loadWarranties();
    } catch (error) {
      console.error('Failed to delete warranty:', error);
      toast.error('Failed to delete warranty');
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      project_material_id: '',
      unit_id: '',
      manufacturer_id: '',
      warranty_start_date: '',
      warranty_end_date: '',
      warranty_provider: '',
      warranty_terms: '',
      coverage_details: '',
      serial_number: '',
      model: ''
    });
  };

  // Open edit modal with warranty data
  const openEditModal = (warranty: WarrantyInterface) => {
    setFormData({
      project_material_id: warranty.project_material_id || '',
      unit_id: warranty.unit_id || '',
      manufacturer_id: warranty.manufacturer_id,
      warranty_start_date: warranty.warranty_start_date,
      warranty_end_date: warranty.warranty_end_date,
      warranty_provider: warranty.warranty_provider,
      warranty_terms: warranty.warranty_terms || '',
      coverage_details: warranty.coverage_details || '',
      serial_number: warranty.serial_number,
      model: warranty.model
    });
    setEditingWarranty(warranty);
  };

  return (
    <div className="min-h-screen">
      {/* Header with Search, Actions and Table Header - Fixed Sticky Section */}
      <div className="sticky top-0 z-30">
        <div className="max-w-[1280px] mx-auto px-0 py-4">
          <div className={`bg-white rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.04)] transition-shadow duration-200 ${isScrolled ? 'shadow-[0_4px_12px_rgba(0,0,0,0.06)]' : ''}`} style={{ border: `0.5px solid ${DesignSystem.colors.border.gray200}` }}>
            {/* Search and Actions Row */}
            <div className="px-6 py-4" style={{ borderBottom: '0.5px solid #eaeaea' }}>
              <div className="flex items-center justify-between">
                {/* Left side - Search and Quick Stats */}
                <div className="flex items-center space-x-6">
                  <div className="relative">
                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#aaa] w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search warranties..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 pr-4 py-2.5 w-80 text-sm bg-[#fafafa] border border-[#eaeaea] rounded-lg focus:outline-none focus:border-[#888] focus:ring-0 transition-all"
                    />
                  </div>
                  
                  {/* Quick Stats */}
                  <div className="flex items-center space-x-4 text-sm">
                    <span className="text-[#888]">
                      {filteredWarranties.length} of {warranties.length} warranties
                    </span>
                  </div>
                </div>

                {/* Right side - Actions */}
                <div className="flex items-center space-x-3">
                  {/* Filters */}
                  <div className="flex items-center space-x-2">
                    <select
                      value={manufacturerFilter}
                      onChange={(e) => setManufacturerFilter(e.target.value)}
                      className="px-3 py-2.5 text-sm bg-[#fafafa] border border-[#eaeaea] rounded-lg focus:outline-none focus:border-[#888] focus:ring-0 transition-all"
                    >
                      <option value="all">All Manufacturers</option>
                      {uniqueManufacturers.map((manufacturer) => (
                        <option key={manufacturer.id} value={manufacturer.id}>
                          {manufacturer.name}
                        </option>
                      ))}
                    </select>

                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="px-3 py-2.5 text-sm bg-[#fafafa] border border-[#eaeaea] rounded-lg focus:outline-none focus:border-[#888] focus:ring-0 transition-all"
                    >
                      <option value="all">All Status</option>
                      <option value="active">Active</option>
                      <option value="expiring">Expiring Soon</option>
                      <option value="expired">Expired</option>
                    </select>
                  </div>
                  
                  {/* Manufacturers */}
                  <button
                    onClick={() => {
                      console.log('🏭 Opening Manufacturer Manager...');
                      setShowManufacturerManager(true);
                    }}
                    className="flex items-center space-x-2 bg-transparent text-[#aaa] border border-[#eaeaea] px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-[#fafafa] hover:border-[#eaeaea] transition-colors"
                  >
                    <FaIndustry className="w-3.5 h-3.5" />
                    <span>Manufacturers</span>
                  </button>

                  {/* Create Button */}
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="flex items-center space-x-2 bg-[#171717] text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-[#333] transition-colors"
                  >
                    <FaPlus className="w-3.5 h-3.5" />
                    <span>Add Warranty</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Table Header */}
            <div className="px-6 py-2">
              <div className="flex items-center text-xs font-medium" style={getTableHeaderStyle()}>
                <div className="flex-1" role="columnheader">Serial Number</div>
                <div className="mx-6 w-32" role="columnheader">Material ID</div>
                <div className="mx-6 w-32" role="columnheader">Model</div>
                <div className="mx-6 w-40" role="columnheader">Manufacturer</div>
                <div className="mx-6 w-28" role="columnheader">Start Date</div>
                <div className="mx-6 w-28" role="columnheader">End Date</div>
                <div className="mx-6 w-32" role="columnheader">Status</div>
                <div className="ml-6 w-20" role="columnheader">Actions</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content - Scrollable Content */}
      <div className="max-w-[1280px] mx-auto px-0 -mt-4">
        <div className="bg-white">
          {isLoading ? (
            <LoadingSpinner text="Loading warranties..." className="py-12" />
          ) : filteredWarranties.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-[#888]">
              <FaShieldAlt className="w-12 h-12 mb-4 text-[#ccc]" />
              {searchQuery || manufacturerFilter !== 'all' || statusFilter !== 'all' ? (
                <>
                  <h3 className="text-lg font-medium mb-2">No warranties found</h3>
                  <p>Try adjusting your search or filters</p>
                </>
              ) : (
                <>
                  <h3 className="text-lg font-medium mb-2">No warranties yet</h3>
                  <p className="mb-4">Add your first warranty to get started</p>
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="flex items-center space-x-2 px-4 py-2 bg-[#171717] text-white rounded-lg hover:bg-[#333] transition-colors"
                  >
                    <FaPlus className="w-4 h-4" />
                    <span>Add Warranty</span>
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="bg-white">
              {filteredWarranties.map((warranty) => (
                <WarrantyRow 
                  key={warranty.id} 
                  warranty={warranty} 
                  onEdit={openEditModal}
                  onDelete={(id) => setShowDeleteModal(id)}
                  getWarrantyStatus={getWarrantyStatus}
                  formatDate={formatDate}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || editingWarranty) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-[#171717]">
                {editingWarranty ? 'Edit Warranty' : 'Add New Warranty'}
              </h3>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setEditingWarranty(null);
                  resetForm();
                }}
                className="text-[#aaa] hover:text-[#555]"
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Serial Number */}
              <div>
                <label className="block text-sm font-medium text-[#444] mb-2">
                  Serial Number
                </label>
                <input
                  type="text"
                  value={formData.serial_number}
                  onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                  className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:border-[#888] focus:ring-0"
                  placeholder="Enter serial number..."
                />
              </div>

              {/* Model Number */}
              <div>
                <label className="block text-sm font-medium text-[#444] mb-2">
                  Model Number
                </label>
                <input
                  type="text"
                  value={formData.model}
                  onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                  className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:border-[#888] focus:ring-0"
                  placeholder="Enter model number..."
                />
              </div>

              {/* Warranty Provider */}
              <div>
                <label className="block text-sm font-medium text-[#444] mb-2">
                  Warranty Provider
                </label>
                <input
                  type="text"
                  value={formData.warranty_provider}
                  onChange={(e) => setFormData({ ...formData, warranty_provider: e.target.value })}
                  className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:border-[#888] focus:ring-0"
                  placeholder="Enter warranty provider..."
                />
              </div>

              {/* Manufacturer */}
              <div>
                <label className="block text-sm font-medium text-[#444] mb-2">
                  Manufacturer
                </label>
                <select
                  value={formData.manufacturer_id}
                  onChange={(e) => setFormData({ ...formData, manufacturer_id: e.target.value })}
                  className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:border-[#888] focus:ring-0"
                >
                  <option value="">Select manufacturer...</option>
                  {availableManufacturers.map((manufacturer) => (
                    <option key={manufacturer.id} value={manufacturer.id}>
                      {manufacturer.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Warranty Start Date */}
              <div>
                <label className="block text-sm font-medium text-[#444] mb-2">
                  Warranty Start Date
                </label>
                <input
                  type="date"
                  value={formData.warranty_start_date}
                  onChange={(e) => setFormData({ ...formData, warranty_start_date: e.target.value })}
                  className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:border-[#888] focus:ring-0"
                />
              </div>

              {/* Warranty End Date */}
              <div>
                <label className="block text-sm font-medium text-[#444] mb-2">
                  Warranty End Date
                </label>
                <input
                  type="date"
                  value={formData.warranty_end_date}
                  onChange={(e) => setFormData({ ...formData, warranty_end_date: e.target.value })}
                  className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:border-[#888] focus:ring-0"
                />
              </div>

              {/* Project Material */}
              <div>
                <label className="block text-sm font-medium text-[#444] mb-2">
                  Project Material (Optional)
                </label>
                <select
                  value={formData.project_material_id}
                  onChange={(e) => setFormData({ ...formData, project_material_id: e.target.value })}
                  className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:border-[#888] focus:ring-0"
                  disabled={isLoadingData}
                >
                  <option value="">Select project material...</option>
                  {projectMaterials.map((material) => (
                    <option key={material.id} value={material.id}>
                      {material.name} - {material.item}
                    </option>
                  ))}
                </select>
                {projectMaterials.length === 0 && !isLoadingData && (
                  <p className="text-xs text-[#888] mt-1">No project materials found. Add materials in the Material Tracker first.</p>
                )}
              </div>

              {/* Unit ID - Optional */}
              <div>
                <label className="block text-sm font-medium text-[#444] mb-2">
                  Unit ID <span className="text-[#aaa] font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={formData.unit_id || ''}
                  onChange={(e) => setFormData({ ...formData, unit_id: e.target.value })}
                  className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:border-[#888] focus:ring-0"
                  placeholder="Leave empty if asset will be added to rack later"
                />
                <p className="text-xs text-[#888] mt-1">
                  Optional: Only fill if you know the specific rack unit UUID. 
                  Assets can be assigned to racks later during installation.
                </p>
              </div>
            </div>

            {/* Warranty Terms */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-[#444] mb-2">
                Warranty Terms
              </label>
              <textarea
                value={formData.warranty_terms}
                onChange={(e) => setFormData({ ...formData, warranty_terms: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:border-[#888] focus:ring-0"
                placeholder="Enter warranty terms..."
              />
            </div>

            {/* Coverage Details */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-[#444] mb-2">
                Coverage Details
              </label>
              <textarea
                value={formData.coverage_details}
                onChange={(e) => setFormData({ ...formData, coverage_details: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:border-[#888] focus:ring-0"
                placeholder="Enter coverage details..."
              />
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setEditingWarranty(null);
                  resetForm();
                }}
                className="flex-1 px-4 py-2 border border-[#eaeaea] text-[#444] rounded-md hover:bg-[#fafafa] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={editingWarranty ? handleUpdate : handleCreate}
                className="flex-1 px-4 py-2 bg-[#171717] text-white rounded-md hover:bg-[#333] transition-colors"
              >
                {editingWarranty ? 'Update' : 'Create'} Warranty
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-[#171717]">Delete Warranty</h3>
              <button
                onClick={() => setShowDeleteModal(null)}
                className="text-[#aaa] hover:text-[#555]"
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>

            <p className="text-[#555] mb-6">
              Are you sure you want to delete this warranty? This action cannot be undone.
            </p>

            <div className="flex space-x-3">
              <button
                onClick={() => setShowDeleteModal(null)}
                className="flex-1 px-4 py-2 border border-[#eaeaea] text-[#444] rounded-md hover:bg-[#fafafa] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(showDeleteModal)}
                className="flex-1 px-4 py-2 bg-[#dc2626] text-white rounded-md hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manufacturer Manager Modal */}
      <ManufacturerManager
        isOpen={showManufacturerManager}
        onClose={() => setShowManufacturerManager(false)}
        onManufacturersChanged={() => {
          loadDropdownData();
        }}
      />
    </div>
  );
};

// Warranty Row Component
const WarrantyRow = ({ 
  warranty, 
  onEdit, 
  onDelete,
  getWarrantyStatus,
  formatDate
}: {
  warranty: WarrantyInterface;
  onEdit: (warranty: WarrantyInterface) => void;
  onDelete: (id: string) => void;
  getWarrantyStatus: (warranty: WarrantyInterface) => { label: string; color: string; icon: any };
  formatDate: (date: string) => string;
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const status = getWarrantyStatus(warranty);
  const StatusIcon = status.icon;

  return (
    <div 
      className={`group hover:bg-[#fafafa]/50 transition-all duration-200 ${
        isHovered ? 'bg-[#fafafa]/50' : ''
      }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="flex items-center px-6 py-4 relative" style={{ borderBottom: '0.5px solid #eaeaea' }}>
        {/* Serial Number */}
        <div className="flex-1 flex items-center min-w-0">
          <div className="flex items-center space-x-2">
            <FaShieldAlt className="w-4 h-4 text-[#888]" />
            <span className="text-sm font-medium text-[#171717] truncate">{warranty.serial_number}</span>
          </div>
        </div>

        {/* Material ID */}
        <div className="mx-6 w-32 flex items-center">
          <span className="text-sm text-[#555] truncate" title={warranty.project_material_id}>
            {warranty.project_material_id ? warranty.project_material_id.slice(0, 8) + '...' : 'N/A'}
          </span>
        </div>

        {/* Model */}
        <div className="mx-6 w-32 flex items-center">
          <span className="text-sm text-[#444] truncate">{warranty.model}</span>
        </div>

        {/* Manufacturer */}
        <div className="mx-6 w-40 flex items-center">
          <span className="text-sm text-[#444] truncate">{warranty.manufacturer.name}</span>
        </div>

        {/* Start Date */}
        <div className="mx-6 w-28 flex items-center">
          <span className="text-sm text-[#555]">{formatDate(warranty.warranty_start_date)}</span>
        </div>

        {/* End Date */}
        <div className="mx-6 w-28 flex items-center">
          <span className="text-sm text-[#555]">{formatDate(warranty.warranty_end_date)}</span>
        </div>

        {/* Status */}
        <div className="mx-6 w-32 flex items-center">
          <div className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${status.color}`}>
            <StatusIcon className="w-3 h-3" />
            <span className="truncate">{status.label}</span>
          </div>
        </div>

        {/* Actions */}
        <div className="ml-6 w-20 flex items-center justify-end space-x-2">
          <button
            onClick={() => onEdit(warranty)}
            className="p-2 text-[#aaa] hover:text-[#171717] hover:bg-[#fafafa] rounded-md transition-all duration-200"
            title="Edit warranty"
          >
            <FaEdit className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDelete(warranty.id)}
            className="p-2 text-[#aaa] hover:text-[#dc2626] hover:bg-red-50 rounded-md transition-all duration-200"
            title="Delete warranty"
          >
            <FaTrash className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}; 